import {
  hashKey,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const persistentContextKeys = [
  "accept-terms",
  "dismissed-stale-flu-data-banner",
  "dismissed-location-banner",
  "dismissed-public-health-emergency-banner",
  "dismissed-critical-threat-banner",
  "notifications-bump",
  "last-alert-definition-banner-date",
  "place-type-migration",
  "ask-history",
  "wingman-default-model",
] as const
type PersistentContextKey =
  | (typeof persistentContextKeys)[number]
  // allow for arbitrary keys
  | (string & Record<never, never>)
  | undefined

interface PersistentContextOptions<T> {
  /** Convert the value into a string for storage. Defaults to `String`. */
  serialize?: (value: T) => string
  /** Parse the stored string back into the desired type. Defaults to identity (cast to `T`). */
  deserialize?: (value: string) => T
}

const usePersistentContext = <T = string>(
  key: PersistentContextKey[],
  options?: PersistentContextOptions<T>
): readonly [T | null | undefined, (value: T) => Promise<T>, boolean] => {
  const queryClient = useQueryClient()
  const serialize = options?.serialize ?? ((value: T) => String(value))
  const deserialize =
    options?.deserialize ?? ((value: string) => value as unknown as T)

  const readFromStorage = (): T | null => {
    const stored = localStorage.getItem(hashKey(key))
    return stored === null ? null : deserialize(stored)
  }

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: readFromStorage,
    // Provide synchronous initial data so callers never see undefined on the first render.
    // Skipped when the key contains undefined (query is disabled anyway).
    initialData: key.includes(undefined) ? undefined : readFromStorage,
    enabled: !key.includes(undefined),
  })

  const { mutateAsync: setValue } = useMutation({
    mutationFn: (value: T) => {
      localStorage.setItem(hashKey(key), serialize(value))
      return Promise.resolve(value)
    },
    onMutate: mutatedData => {
      const previousData = queryClient.getQueryData<T | null>(key)
      queryClient.setQueryData(key, mutatedData)
      return previousData
    },
    onError: (_, __, rollback) => {
      queryClient.setQueryData(key, rollback)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key }).catch(console.error)
    },
  })

  return [data, setValue, isLoading || key.includes(undefined)] as const
}

export default usePersistentContext
